import { jwtDecode } from "jwt-decode";

// =============================================================================
// Permissões no frontend.
//
// Um modelo só: a chave do cargo (RBAC), no formato "modulo.recurso.acao", vinda
// de `/api/me/access`. Quem é da equipe da plataforma (POWER_USER) não precisa de
// chave — passa em tudo. O tipo legado (ADMIN/USER) não decide mais nada aqui.
//
// O gate de verdade é sempre o do servidor — o que está aqui decide o que
// DESENHAR, nunca o que é permitido.
// =============================================================================

/** Equipe da plataforma: `access.permissionId` vem de `/api/me/access`. */
export const isPlatformStaff = (access) =>
  String(access?.permissionId || "").toUpperCase() === "POWER_USER";

/**
 * `access` é o objeto de /api/me/access ({ permissions, dataScope, ... }).
 *
 * Enquanto ele não chegou (primeiro render), devolvemos `true`: esconder o
 * menu inteiro por meio segundo a cada carregamento é pior do que mostrar um
 * item que o servidor vai recusar depois. A recusa real acontece no backend.
 */
export const hasPermission = (access, permissionKey) => {
  if (!permissionKey) return true;
  if (!access) return true;
  if (isPlatformStaff(access)) return true;

  const list = Array.isArray(access.permissions) ? access.permissions : null;
  if (!list) return true;

  return list.includes("*") || list.includes(permissionKey);
};

export const hasAnyPermission = (access, keys = []) =>
  (Array.isArray(keys) ? keys : [keys]).some((key) => hasPermission(access, key));

export const hasAllPermissions = (access, keys = []) =>
  (Array.isArray(keys) ? keys : [keys]).every((key) => hasPermission(access, key));

/** Chave que separa "vê os dados de toda a empresa" de "vê só os próprios". */
export const VIEW_ALL_PERMISSION = "data.viewAll";

/**
 * Diferente de `hasPermission`, aqui "ainda não sei" vale NÃO: as telas decidem
 * com isto o que pedir ao servidor (ex.: "Todos" × "só os meus"), e começar
 * pedindo demais seria pior do que começar restrito.
 */
export const canViewAllData = (access) =>
  Boolean(access) && hasPermission(access, VIEW_ALL_PERMISSION);

/**
 * Mapa "chave → chaves que ela já inclui", montado a partir do catálogo de
 * `GET /roles/catalog` (campo `implies` de cada permissão). A fonte da regra é o
 * backend (`IMPLIED_PERMISSIONS`); aqui só se desenha o que ele já aplica.
 */
export const buildImpliedMap = (catalog) => {
  const map = {};
  (catalog?.groups || []).forEach((group) =>
    (group.permissions || []).forEach((permission) => {
      if (Array.isArray(permission.implies) && permission.implies.length) {
        map[permission.key] = permission.implies;
      }
    }),
  );
  return map;
};

/** Acrescenta à lista tudo o que as chaves dela já incluem (fecho transitivo). */
export const expandImpliedKeys = (keys, impliedMap = {}) => {
  const list = Array.isArray(keys) ? keys : [];
  if (list.includes("*")) return ["*"];
  const seen = new Set(list);
  const queue = [...list];
  while (queue.length) {
    const key = queue.shift();
    (impliedMap[key] || []).forEach((implied) => {
      if (!seen.has(implied)) {
        seen.add(implied);
        queue.push(implied);
      }
    });
  }
  return Array.from(seen);
};

/** "own" | "team" | "company" — usado por telas que mostram filtro de escopo. */
export const dataScopeOf = (access) => access?.dataScope || "company";

export const getUserId = () => {
  try {
    const token = localStorage.getItem("token");
    const { user } = jwtDecode(token);

    return user.id;
  } catch (error) {
    return 0;
  }
};

export const removeToken = () => {
  localStorage.removeItem("token");
  window.location.href = "/login";
};
